import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { Table } from "../";
import { IColum } from "../../../utils/copyToClipboardInXlsFormat";
import usePagination from "../../../hooks/usePagination";
import WatchlistNoteForm from "../WatchlistNoteForm";
import { IDbWatchlistNotes } from "../../../api/dbModelsInterfaces";
import { COMPLIANCE_API_URLS } from "../../../api/compliance";
import { useAdminProviderDataContext } from "../../../context";

export type TGetInitialValues = (editIds: number[]) => {
  reason: string;
  measure_taken: string;
  compliance_entries_id?: number;
  accounts_id?: number;
  email?: string | undefined;
  assignedEmail?: string | undefined;
};

interface WatchlistNotesTableProps {
  rows: IDbWatchlistNotes[];
  isLoading: boolean;
  onFetchNotes: (
    query: URLSearchParams,
    signal: AbortSignal | undefined
  ) => Promise<{
    data: {
      results: {
        count: number;
      };
    };
  }>;
  onFetchRequest: () => void;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFetchSuccess: (data: any) => void;
  onFetchError: () => void;
  getInitialValues: TGetInitialValues;
  getColumns: (onEditNote: (ids: number[]) => void) => IColum[];
  queryKey: (string | number)[];
  withEmailSelect?: boolean;
  withButtonVariant?: boolean;
}

const WatchlistNotesTable: React.FC<WatchlistNotesTableProps> = ({
  rows,
  isLoading,
  onFetchNotes,
  onFetchRequest,
  onFetchSuccess,
  onFetchError,
  getInitialValues,
  getColumns,
  queryKey,
  withEmailSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editIds, setEditIds] = useState<number[]>([]);
  const dispatch = useDispatch();
  const pagination = usePagination();

  const { isError, error } = useQuery({
    queryKey: [COMPLIANCE_API_URLS.getSearchNotes, ...queryKey],
    retry: false,
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());

      dispatch(onFetchRequest());
      const response = await onFetchNotes(query, signal);
      dispatch(onFetchSuccess(response.data));
      pagination.setCount(response.data.results.count);
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(onFetchError());
    }
  }, [isError, error]);

  const onEditNote = (ids: number[]) => {
    setEditIds(ids);
    setIsOpen(true);
  };

  const {
    user: {
      allowed_features: { access_edit_watchlist_notes },
    },
  } = useAdminProviderDataContext();

  const watchlistNotesColumns = getColumns(onEditNote);
  if (!access_edit_watchlist_notes) watchlistNotesColumns.shift();

  return (
    <div>
      {access_edit_watchlist_notes && (
        <Button onClick={() => setIsOpen(true)} size="small" variant="contained" color="primary">
          Add watchlist note
        </Button>
      )}

      <WatchlistNoteForm
        withEmailSelect={withEmailSelect}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        editIds={editIds}
        setEditIds={setEditIds}
        getInitialValues={getInitialValues}
      />

      <Table
        pagination={pagination}
        columns={watchlistNotesColumns}
        data={rows}
        extractKey={(row) => row.id || (row.ids as []).join(", ")}
        loading={isLoading}
        theadTitle="Watchlist notes"
        csv_table_name="Watchlist notes"
        noResultsMessage="There are no watchlist notes"
      />
    </div>
  );
};

export default WatchlistNotesTable;
